import { SerializedInventoryMeterReadingGroup } from '@enmon/commons/lib/tools';
import { Button, Card, Col, Form, Input, Row, Space, message } from 'antd';
import { FormItem } from 'components/Form/FormItem';
import { QrReader } from 'components/qr_reader/QrReader';
import { FC } from 'react';
import { useServices } from 'services/useServices';
import { useIntl, FormattedMessage } from 'react-intl';
import { SerializedMeter } from '@enmon/commons/lib/enmon';
import { AddMeterForm as messages } from '../../messages';
import { InputNumber } from '../../../../components/Input/InputNumber';
import { useStore as useInventorizeStore } from '../../../inventorize_meter/useStore';

interface AddMeterFormProps {
  group: SerializedInventoryMeterReadingGroup;
  isLoading: boolean;
  onAddMeter: (meterIds: number[]) => Promise<void>;
}

interface AddMeterFormFields {
  apiKey: string;
  toolsId: number;
}

export const AddMeterForm: FC<AddMeterFormProps> = ({ group, isLoading, onAddMeter }) => {
  const { location } = useInventorizeStore();
  const [form] = Form.useForm<AddMeterFormFields>();
  const {
    api: { inventoryLocationsApiClient },
  } = useServices();
  const intl = useIntl();

  const handleScan = (result?: string | null) => {
    if (result) {
      form.setFieldsValue({ apiKey: result });
      message.success(intl.formatMessage(messages.scanSuccess));
    }
  };

  const handleAddMeter = async (values: AddMeterFormFields) => {
    const { apiKey, toolsId } = values;

    if (!apiKey && !toolsId) {
      message.error(intl.formatMessage(messages.validatorRequired));
      return;
    }

    let meter: SerializedMeter | undefined;
    try {
      const meters = await inventoryLocationsApiClient.getLocationMeters({
        id: location?.id,
        filter: {
          ...(apiKey && { apiKeys: [apiKey] }),
          ...(toolsId && { toolsIds: [toolsId] }),
        },
      });

      meter = meters?.result[0];

      if (!meter?.toolsId) {
        message.error(intl.formatMessage(messages.notFound));
        return;
      }
    } catch (error) {
      message.error(intl.formatMessage(messages.findError));
      return;
    }

    const newMeterToolsId = Number(meter.toolsId);

    if (group.inventory_meters?.some(m => m.id === newMeterToolsId)) {
      message.error(intl.formatMessage(messages.alreadyInGroup));
      return;
    }
    const currentMeterIds =
      group.inventory_meters
        ?.map(m => m.id)
        .filter((currentMeterId: number | undefined): currentMeterId is number => currentMeterId !== undefined) || [];
    await onAddMeter([...currentMeterIds, newMeterToolsId]);
    form.resetFields();
  };

  return (
    <Card title={<FormattedMessage {...messages.title} />}>
      <Form form={form} layout="horizontal" onFinish={handleAddMeter}>
        <Row justify="center" style={{ marginBottom: 16 }}>
          <Col xl={20} xs={24}>
            <QrReader onScan={handleScan} />
          </Col>
        </Row>
        <FormItem
          label={<FormattedMessage {...messages.meterApiKeyLabel} />}
          name={'apiKey' satisfies keyof AddMeterFormFields}
          tooltip={intl.formatMessage(messages.meterApiKeyTooltip)}
        >
          <Input disabled placeholder={intl.formatMessage(messages.meterApiKeyPlaceholder)} />
        </FormItem>
        <FormItem
          label={<FormattedMessage {...messages.strapiIdLabel} />}
          name={'toolsId' satisfies keyof AddMeterFormFields}
          tooltip={intl.formatMessage(messages.strapiIdTooltip)}
        >
          <InputNumber controls={false} placeholder={intl.formatMessage(messages.strapiIdPlaceholder)} />
        </FormItem>
        <Row justify="end">
          <Col>
            <Space>
              <Button onClick={() => form.resetFields()}>
                <FormattedMessage {...messages.cancel} />
              </Button>
              <Button htmlType="submit" loading={isLoading} type="primary">
                <FormattedMessage {...messages.addMeter} />
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
